/**
 * @module Shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

import { Directive, HostListener, Input, ElementRef, OnInit, Renderer2 } from '@angular/core';
import { DollarPipe } from '@support/shared/widgets/pipes/dollar/dollar.pipe';

@Directive({
  selector: '[tmoAllowedInput]',
  providers: [DollarPipe],
})
/*
 *  This directive will take pattern and accept only allowed characters as given in the pattern
 */
export class AllowedInputDirective implements OnInit {
  @Input() private pattern: string;
  private previousInput: number;
  private oldValue: string;
  @Input() private overwrite: boolean;
  private selection: string;
  constructor(
    private elRef: ElementRef,
    private currencyPipe: DollarPipe,
    private renderer: Renderer2
  ) {}

  /*
   *  input Listner to listen key/paste event and apply pattern to the input
   */
  /* tslint:disable:cyclomatic-complexity */
  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    const backspace = 8;
    const leftKey = 37;
    const rightKey = 39;
    const tab = 9;
    const enter = 13;
    const ctrl = 17;
    const pasteV = 86;
    const deleteKey = 46;
    const shift = 16;
    // Allows Backspace, Left and Right keys in the inputbox
    if (
      [backspace, leftKey, rightKey, tab, enter, ctrl, deleteKey, shift].includes(event.keyCode) ||
      (this.previousInput === ctrl && event.keyCode === pasteV)
    ) {
      this.previousInput = event.keyCode;
      return;
    }
    this.previousInput = event.keyCode;
    let current: string = this.elRef.nativeElement.value;
    if (
      this.elRef.nativeElement.id !== 'dollar_amount' &&
      this.pattern &&
      this.elRef.nativeElement.value != this.oldValue &&
      current != '' &&
      !current.match(this.pattern)
    ) {
      this.elRef.nativeElement.value = this.oldValue || '';
      event.preventDefault();
      return;
    }
    this.oldValue = current;

    if (this.elRef && this.elRef.nativeElement) {
      if (this.elRef.nativeElement.nodeName === 'P-CALENDAR') {
        const calendarInput = this.renderer.selectRootElement('[id="tmo-calendar-custom-date"]');
        current = calendarInput.value;
      }
      if (current !== undefined) {
        const next: string = event.key;
        const inputTarget = <HTMLInputElement>event.target;
        this.selection =
          this.overwrite &&
          inputTarget.selectionStart !== inputTarget.selectionEnd &&
          inputTarget.value.substring(inputTarget.selectionStart, inputTarget.selectionEnd);
        const beforeEndSubString = `${current.substring(0, inputTarget.selectionStart)}${next}`;
        const overwritenValue =
          this.selection && `${beforeEndSubString}${current.substring(inputTarget.selectionEnd)}`;
        const afterOverwriteCursorPosition = beforeEndSubString.length;
        if (this.pattern && next && this.elRef.nativeElement.id !== 'dollar_amount') {
          if (
            !this.selection &&
            !String(
              current.slice(0, inputTarget.selectionStart) +
                next +
                current.slice(inputTarget.selectionEnd, current.length)
            ).match(this.pattern)
          ) {
            event.preventDefault();
          } else if (overwritenValue) {
            if (String(overwritenValue).match(this.pattern)) {
              this.elRef.nativeElement.value = overwritenValue;
              inputTarget.setSelectionRange(
                afterOverwriteCursorPosition,
                afterOverwriteCursorPosition
              );
            }
            event.preventDefault();
          }
        }
      }
    }
  }

  /*
   *  ngOnInit will be called at the initaialization of directive and set transform value
   */
  public ngOnInit(): void {
    if (
      this.elRef &&
      this.elRef.nativeElement &&
      this.elRef.nativeElement.value !== '' &&
      this.elRef.nativeElement.id === 'dollar_amount'
    ) {
      this.elRef.nativeElement.value = this.currencyPipe.transform(this.elRef.nativeElement.value);
    }
  }

  /*
   *  onFocus , listen the input key event and check for amount input only and transform the value
   *  to dollar and decimal
   */
  @HostListener('focus', ['$event.target.value'])
  public onFocus(value: string): void {
    if (this.elRef && this.elRef.nativeElement && this.elRef.nativeElement.id === 'dollar_amount') {
      value = value.replace(/\`|\$|\s/g, '');
      this.elRef.nativeElement.value = this.currencyPipe.parse(value); // opposite of transform
    }
  }

  /*
   *  onBlur will parse the input value only for amount input
   */
  @HostListener('blur', ['$event.target.value'])
  public onBlur(value: string): void {
    value = value.replace(/\`|\$|\s/g, '');
    if (
      this.elRef &&
      this.elRef.nativeElement &&
      this.elRef.nativeElement.id === 'dollar_amount' &&
      this.elRef.nativeElement.value !== ''
    ) {
      this.elRef.nativeElement.value = this.currencyPipe.transform(value);
      this.oldValue = value;
    }
  }

  @HostListener('ngModelChange', ['$event'])
  public onChange(value: string): void {
    if (
      this.elRef &&
      this.elRef.nativeElement &&
      this.elRef.nativeElement.id === 'dollar_amount' &&
      !String(Number(value)).match(this.pattern)
    ) {
      this.elRef.nativeElement.value = this.oldValue;
    }
  }
}
